/**
 * Navigation Types for FlowRide
 * Defines types and enums for the navigation system
 */

/**
 * Latitude, Longitude coordinate pair
 */
export type LatLng = [ number, number ];

/**
 * Longitude, Latitude coordinate pair (GeoJSON order)
 */
export type LngLat = [ number, number ];

/**
 * Convert GpsData to [longitude, latitude] format (GeoJSON order)
 */
export function toLngLat( gpsData: GpsData ): LngLat {
    return [ gpsData.longitude, gpsData.latitude ];
}

/**
 * Convert GpsData to [latitude, longitude] format
 */
export function toLatLng( gpsData: GpsData ): LatLng {
    return [ gpsData.latitude, gpsData.longitude ];
}

/**
 * GpsData with computed properties for easy coordinate access
 * Adds pointLngLat and pointLatLng getters
 */
export interface GpsDataWithCoords extends GpsData {

    /** Get coordinates as [longitude, latitude] (GeoJSON format) */
    readonly "pointLngLat": LngLat;

    /** Get coordinates as [latitude, longitude] */
    readonly "pointLatLng": LatLng;
}

/**
 * Adds computed coordinate properties to a GpsData object
 * Returns a new object with pointLngLat and pointLatLng properties
 */
export function withGpsCoords<T extends GpsData>( gpsData: T ): T & GpsDataWithCoords {
    const coords = {
        get "pointLngLat"(): LngLat {
            return [ gpsData.longitude, gpsData.latitude ];
        },
        get "pointLatLng"(): LatLng {
            return [ gpsData.latitude, gpsData.longitude ];
        },
    };

    return Object.assign( gpsData, coords ) as T & GpsDataWithCoords;
}

/**
 * Navigation modes supported by the application
 */
export enum NavigationMode {

    /** Exploration mode: Shows device orientation cone, GPS prediction in free mode */
    "EXPLORATION" = "exploration",

    /** Constrained navigation mode: Follows route line, position constrained to route */
    "CONSTRAINED_NAVIGATION" = "constrained_navigation",
}

/**
 * Base GPS data structure with common properties
 */
export interface GpsData {

    /** Latitude in degrees */
    "latitude": number;

    /** Longitude in degrees */
    "longitude": number;

    /** Altitude in meters (if available) */
    "altitude": number;

    /** GPS heading in degrees [0, 360) (if available) */
    "heading": number;

    /** Speed in meters/second (if available) */
    "speed": number;

    /** Timestamp of the GPS reading */
    "timestamp": number;
}

/**
 * GPS data structure (raw position from device)
 */
export interface RawGpsData extends GpsData {

    /** Accuracy in meters */
    "accuracy": number;

    /** Altitude accuracy in meters (if available) */
    "altitudeAccuracy": number | null;
}

/**
 * Filtered GPS data (after Kalman filtering)
 */
export interface FilteredGpsData extends GpsData {

    /** Estimated accuracy in meters */
    "accuracy": number;
}

/**
 * GPS prediction data (dead reckoning)
 */
export interface GpsPredictionData extends GpsData {

    /** Whether prediction is constrained to a route line */
    "constrained": boolean;

    /** Confidence level of the prediction [0, 1] */
    "confidence": number;
}

/**
 * Compass data (device orientation)
 */
export interface CompassData {

    /** Device heading in degrees [0, 360) */
    "heading": number;

    /** Whether the compass is calibrated */
    "calibrated": boolean;

    /** Calibration offset applied (in degrees) */
    "offset": number;

    /** Timestamp of the reading */
    "timestamp": number;
}

/**
 * Camera configuration for the map
 */
export interface CameraConfig {

    /** Camera pitch angle in degrees */
    "pitch": number;

    /** Camera bearing/rotation in degrees [0, 360) */
    "bearing": number;

    /** Camera zoom level */
    "zoom": number;

    /** Center coordinates [longitude, latitude] */
    "center": [number, number];

    /** Animation duration in milliseconds */
    "duration": number;
}

/**
 * Event types for navigation services
 */
export enum NavigationEventType {

    /** Raw GPS data updated */
    "RAW_GPS_UPDATE" = "raw_gps_update",

    /** Filtered GPS data updated */
    "FILTERED_GPS_UPDATE" = "filtered_gps_update",

    /** GPS prediction updated */
    "GPS_PREDICTION_UPDATE" = "gps_prediction_update",

    /** Compass data updated */
    "COMPASS_UPDATE" = "compass_update",

    /** Compass calibration changed */
    "COMPASS_CALIBRATION" = "compass_calibration",

    /** Navigation mode changed */
    "MODE_CHANGE" = "mode_change",

    /** Camera configuration changed */
    "CAMERA_UPDATE" = "camera_update",
}

/**
 * Base event structure for navigation events
 */
export interface NavigationEvent<T = unknown> {

    /** Event type */
    "type": NavigationEventType;

    /** Event data */
    "data": T;

    /** Event timestamp */
    "timestamp": number;
}

/**
 * Route line segment for constrained navigation
 */
export interface RouteSegment {

    /** Start point [longitude, latitude] */
    "start": [number, number];

    /** End point [longitude, latitude] */
    "end": [number, number];

    /** Direction of the segment in degrees [0, 360) */
    "bearing": number;

    /** Length of the segment in meters */
    "length": number;
}

/**
 * Calibration mode for compass
 */
export enum CalibrationMode {

    /** No calibration active */
    "NONE" = "none",

    /** Automatic calibration based on GPS direction */
    "AUTOMATIC" = "automatic",

    /** Manual calibration by user */
    "MANUAL" = "manual",
}

/**
 * Calibration state
 */
export interface CalibrationState {

    /** Current calibration mode */
    "mode": CalibrationMode;

    /** Offset applied to compass readings (in degrees) */
    "offset": number;

    /** Whether calibration is active and valid */
    "active": boolean;

    /** Timestamp of last calibration */
    "lastCalibration": number | null;

    /** Number of calibration samples collected (for automatic) */
    "sampleCount": number;
}
